import { Gif, View } from '@/components'
import { Attachment, Channel } from 'stream-chat'
import { MessageAttachment } from './MessageAttachment'
import { useMessageContext } from 'stream-chat-react'
import { memo } from 'react'
import { MessageProps } from '../types'
import { Chat, theme } from '@/config'
import { ChatFileTypes, GiphyAttachment, isChatGroup } from '@packages/chat'
import { useIsTablet } from '@/hooks'
import { ImageGrid } from './ImageGrid'
import { AudioPlayer } from './AudioPlayer'

const MAX_IMAGE_SIZE = Chat.Constants.maxImageSize

export const MessageAttachmentsWithContext = ({
  message,
  isMyMessage,
  channel,
}: MessageProps & {
  channel: Channel
}) => {
  const attachments = message?.attachments || []
  const isTablet = useIsTablet()

  const images = attachments.filter(
    attachment => attachment.type === ChatFileTypes.Image && !!attachment.image_url && !attachment?.title_link && Number(attachment?.file_size) <= MAX_IMAGE_SIZE,
  )

  const audioMessages = attachments.filter(
    attachment => attachment.type === ChatFileTypes.Audio || attachment.type === ChatFileTypes.VoiceRecording,
  )

  const files = attachments.filter(
    attachment => (attachment.type !== ChatFileTypes.Image &&
      attachment.type !== ChatFileTypes.Giphy &&
      attachment.type !== ChatFileTypes.Audio &&
      attachment.type !== ChatFileTypes.VoiceRecording &&
      !attachment?.title_link) || (attachment.type === ChatFileTypes.Image && Number(attachment?.file_size) >= MAX_IMAGE_SIZE),
  )

  const gifs = attachments.filter(
    attachment => attachment.type === ChatFileTypes.Giphy,
  )

  if (!images.length && !files.length && !gifs.length && !audioMessages.length) {
    return null
  }

  async function handleDownload(attachment: Attachment) {
    alert('Coming soon')
  }

  return (
    <View style={['column', 'gap:0.5', { marginTop: theme.spacing.value(isChatGroup(channel) && !isMyMessage() ? 0.5 : 0) }]}>
      {gifs.length > 0 && (
        <View style={['flex', 'gap:0.5']}>
          {gifs.map((item) => {
            const gif = item as GiphyAttachment
            const id = gif?.custom?.giphy_id
            return (<Gif key={id} id={id} width={isTablet ? 200 : 250} />)
          })}
        </View>
      )}

      {audioMessages.length > 0 && (
        <View style={['flex', 'gap:0.5', 'column']}>
          {audioMessages.map((audio, index) => (
            <AudioPlayer
              key={index}
              id={`${message?.id ?? 'message'}-${index}`}
              audioUrl={audio.asset_url || (audio as any).file_url || ''}
              duration={audio.duration}
              avatarUrl={message?.user?.image}
              userName={message?.user?.name}
              waveformData={audio.waveform_data}
              isMyMessage={!!isMyMessage?.()}
            />
          ))}
        </View>
      )}

      {files.length > 0 && (
        <View style={['flex', 'gap:0.5', 'column']}>
          {files.map((file, index) => (
            <MessageAttachment
              key={index}
              attachment={file}
              onPress={() => handleDownload(file)}
              debugName={`MessageAttachment-${file.title}`}
            />
          ))}
        </View>
      )}

      {images.length > 0 && (
        <ImageGrid images={images} />
      )}
    </View>
  )
}

function areEqual(
  prevProps: MessageProps,
  nextProps: MessageProps,
) {
  return (
    prevProps?.message?.attachments?.length ===
    nextProps?.message?.attachments?.length
  )
}

const MemoizedMessageAttachments = memo(MessageAttachmentsWithContext, areEqual)

export const MessageAttachments = ({ channel }: { channel: Channel }) => {
  const props = useMessageContext()
  return <MemoizedMessageAttachments {...props} channel={channel} />
}
